import api from './axios';

export const tokensAPI = {
  /**
   * Создать новый токен
   * @param {Object} data - Данные токена
   * @param {string} data.proxyId - ID прокси
   * @param {Array} data.ports - Массив портов [{protocol: 'tcp', port: 8086}]
   * @param {boolean} data.allowAnyNonPrivileged - Разрешить любые непривилегированные порты
   * @param {number} data.maxPorts - Максимальное количество портов
   * @param {string} data.duration - Длительность токена (1h, 24h, 30m, 1d)
   * @param {Array<number>} data.reservationIds - Массив ID резерваций портов (опционально)
   * @returns {Promise} Созданный токен
   */
  create: async (data) => {
    const response = await api.post('/api/v1/tokens', data);
    return response.data;
  },

  /**
   * Получить список токенов
   * @param {Object} params - Параметры запроса
   * @param {boolean} params.activeOnly - Только активные токены
   * @param {boolean} params.includeExpired - Включить истекшие токены
   * @param {number} params.limit - Лимит количества токенов
   * @param {number} params.offset - Смещение
   * @returns {Promise} Список токенов
   */
  getList: async (params = {}) => {
    // Явно преобразуем булевы значения в строки для корректной работы с бэкендом
    const queryParams = {
      ...params,
      activeOnly: params.activeOnly ? 'true' : 'false',
      includeExpired: params.includeExpired ? 'true' : 'false'
    };
    const response = await api.get('/api/v1/tokens', { params: queryParams });
    return response.data;
  },

  /**
   * Получить конкретный токен
   * @param {string|number} tokenIdentifier - ID токена или JTI
   * @returns {Promise} Данные токена
   */
  getById: async (tokenIdentifier) => {
    const response = await api.get(`/api/v1/tokens/${tokenIdentifier}`);
    return response.data;
  },

  /**
   * Удалить (отозвать) токен
   * @param {string|number} tokenIdentifier - ID токена или JTI
   * @returns {Promise} Результат операции
   */
  delete: async (tokenIdentifier) => {
    const response = await api.delete(`/api/v1/tokens/${tokenIdentifier}`);
    return response.data;
  },

  /**
   * Резервировать порты
   * @param {Object} params - Параметры резервации
   * @param {string} params.proxyId - ID прокси сервера
   * @param {Array} params.ports - Массив портов для резервации [{protocol: 'tcp', port: 8080}]
   * @param {number} params.ttlSeconds - Время жизни резервации в секундах (по умолчанию 300 = 5 минут)
   * @returns {Promise} Результат резервации {reservations: [{id, protocol, port, expiresAt}], expiresAt, ttlSeconds}
   */
  reservePorts: async (params) => {
    const response = await api.post('/api/v1/tokens/reserve-ports', params);
    return response.data;
  },

  /**
   * Освободить резервации портов
   * @param {Object} params - Параметры освобождения
   * @param {Array<number>} params.reservationIds - Массив ID резерваций для освобождения
   * @param {string} params.proxyId - ID прокси (освободить все резервации на этом прокси)
   * @param {boolean} params.releaseAll - Освободить все резервации пользователя
   * @returns {Promise} Результат операции {released: number, failed: number, details: Array}
   */
  releaseReservations: async (params) => {
    const response = await api.post('/api/v1/tokens/release-reservations', params);
    return response.data;
  },

  /**
   * Получить активные резервации пользователя
   * @param {string} proxyId - ID прокси (опционально, для фильтрации)
   * @returns {Promise} Список резерваций {reservations: Array}
   */
  getReservations: async (proxyId = null) => {
    const params = proxyId ? { proxyId } : {};
    const response = await api.get('/api/v1/tokens/reservations', { params });
    return response.data;
  },

  /**
   * Освободить конкретную резервацию
   * @param {number} reservationId - ID резервации
   * @returns {Promise} Результат операции {success: boolean, message: string}
   */
  deleteReservation: async (reservationId) => {
    const response = await api.delete(`/api/v1/tokens/reservations/${reservationId}`);
    return response.data;
  },

  /**
   * Проверить доступность портов
   * @param {Array} ports - Массив портов для проверки [{protocol: 'tcp', port: 8086}]
   * @param {string} proxyId - ID прокси, для которого проверяем порты
   * @returns {Promise} Результат проверки {available: boolean, conflicts: Array}
   */
  checkPorts: async (ports, proxyId) => {
    const response = await api.post('/api/v1/tokens/check-ports', { ports, proxyId });
    return response.data;
  },

  /**
   * Найти доступные порты на прокси сервере
   * @param {Object} options - Опции поиска
   * @param {string} options.proxyId - ID прокси сервера
   * @param {Array} options.protocols - Массив протоколов ['tcp', 'udp']
   * @param {number} options.count - Количество портов для каждого протокола
   * @param {number} options.startPort - Начальный порт для поиска (по умолчанию 1024)
   * @param {number} options.endPort - Конечный порт для поиска (по умолчанию 65535)
   * @returns {Promise} Найденные порты {ports: [{protocol, port}], totalFound: number}
   */
  findAvailablePorts: async (options) => {
    const response = await api.post('/api/v1/tokens/find-available-ports', options);
    return response.data;
  },

  /**
   * Получить информацию о разрешённых портах
   * @param {string} proxyId - ID прокси сервера
   * @returns {Promise} Информация о разрешённых портах {proxy: {allowedPorts}, userOverrides: []}
   */
  getPortRanges: async (proxyId) => {
    const response = await api.get(`/api/v1/tokens/port-ranges/${proxyId}`);
    return response.data;
  }
};
